<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Auflösungs- und Dateigrößenspezifikationen für
            360°-Virtual-Tour-Bilder bestimmen, wie scharf Ihre Tour auf
            verschiedenen Geräten aussieht, wie schnell sie lädt und ob sie die
            technischen Anforderungen von Plattformen wie Matterport, Google
            Street View, Kuula und Travvir erfüllt. Die falsche Auflösung zu
            verwenden kostet Sie entweder Qualität (zu niedrig) oder
            Ladegeschwindigkeit und Speicherkosten (zu hoch). Dieser Leitfaden
            deckt die genauen Spezifikationen ab, die Sie für jede wichtige
            Plattform und jeden Anwendungsfall benötigen.
          </p>
        </section>

        <section>
          <h2>360°-Bildauflösung verstehen</h2>

          <h3>
            Warum 360°-Bilder eine höhere Auflösung als Standardfotos benötigen
          </h3>
          <p>
            Ein Standardfoto wird als flaches Bild angezeigt. Wenn Sie ein
            Standardfoto in voller Bildschirmgröße auf einem 4K-Monitor (3840 ×
            2160 Pixel) anzeigen, füllt das gesamte Bild den Bildschirm.
          </p>
          <p>
            Ein 360°-Foto wird anders dargestellt: Der Viewer zeigt zu einem
            bestimmten Zeitpunkt nur einen Teil des Gesamtbildes an – ungefähr
            das Äquivalent des Sichtfeldes eines Standardfotos, aus dem größeren
            sphärischen Quellbild extrahiert. Für einen 360°-Viewer mit einem
            horizontalen 90°-Sichtfeld zeigen Sie 90° einer 360°-Kugel an –
            <strong>nur 25 % der gesamten Bildbreite</strong> ist auf einmal
            sichtbar.
          </p>
          <p>
            Das bedeutet, dass ein 360°-Foto beim Betrachten genauso scharf wie
            ein Standardfoto aussehen muss,
            <strong>4× die Auflösung</strong> des äquivalenten Standardbildes
            benötigt. Ein 360°-Foto, das in einem Virtual-Tour-Viewer gut
            aussieht, muss mindestens 8.000 × 4.000 Pixel (32 Megapixel) haben.
          </p>

          <h3>Das äquirektanguläre Seitenverhältnis</h3>
          <p>
            360°-Fotos in äquirektangulärer Projektion haben immer ein
            <strong>2:1-Seitenverhältnis</strong> – doppelt so breit wie hoch.
            Gebräuchliche Standardgrößen: <strong>4096 × 2048</strong> (Minimum
            für akzeptable Qualität), <strong>8192 × 4096</strong> (Standard
            hohe Qualität), <strong>11520 × 5760</strong> (hochendige
            Profi-Qualität), <strong>16384 × 8192</strong> (maximale Qualität,
            erfordert fortschrittliche Kamera oder Stitching). Jedes Bild, das
            das 2:1-Verhältnis nicht beibehält, wird in 360°-Viewern verzerrt
            erscheinen.
          </p>
        </section>

        <section>
          <h2>Plattformspezifische Auflösungsanforderungen</h2>

          <h3>Google Street View / Google Business Profile</h3>
          <p>
            <strong>Mindestauflösung:</strong> 7,5 Megapixel (keine minimale
            Abmessung angegeben, aber Dateien unter ca. 3000 × 1500 werden
            abgelehnt). <strong>Maximale Upload-Größe:</strong> 75 Megapixel.
            <strong>Empfohlene Auflösung:</strong> 8192 × 4096 (33 Megapixel)
            für optimale Anzeigequalität. <strong>Dateiformat:</strong> JPEG mit
            eingebetteten XMP Photo Sphere-Metadaten. Google komprimiert alle
            Uploads bei der Aufnahme.
          </p>

          <h3>Facebook 360°-Fotos</h3>
          <p>
            <strong>Mindestauflösung:</strong> 2048 × 1024.
            <strong>Maximale Upload-Größe:</strong> 30 MB.
            <strong>Empfohlene Auflösung:</strong> 6000 × 3000 für gute Qualität
            im Facebook-Viewer. <strong>Anmerkung:</strong> Facebook wendet beim
            Upload eine aggressive Komprimierung an, was die sichtbare Qualität
            erheblich reduziert.
          </p>

          <h3>YouTube 360°-Video</h3>
          <p>
            360°-Video, nicht Fotos, aber zur Referenz:
            <strong>minimale effektive Auflösung</strong> für akzeptable
            Qualität ist 5760 × 2880 (8K). Niedrigere Auflösungen liefern
            aufgrund der oben beschriebenen Anzeige-Methode deutlich pixelige
            Ergebnisse.
          </p>

          <h3>Travvir Virtual Tours</h3>
          <p>
            Travvir akzeptiert äquirektanguläre 360°-Fotos mit
            <strong>empfohlener Auflösung von 8192 × 4096</strong> für beste
            Qualität im Tour-Viewer. Die Plattform verarbeitet Komprimierung und
            Lieferungsoptimierung automatisch und stellt je nach Gerät und
            Verbindungsgeschwindigkeit des Betrachters geeignete Qualitätsstufen
            bereit. Dateien bis zu 50 MB werden im JPEG-Format akzeptiert.
          </p>

          <h3>Matterport</h3>
          <p>
            Matterport verwendet seine eigene Aufnahmehardware und seinen
            eigenen Prozess – hochgeladene 360°-Bilder für die Verwendung in
            Matterport-Touren erfordern die Matterport Link-Funktion oder die
            Integration von Drittanbietern. Für die Standardnutzung wird die
            Matterport-Tour-Qualität durch die Aufnahmehardware bestimmt, nicht
            durch die Upload-Auflösung.
          </p>
        </section>

        <section>
          <h2>Dateiformat und Komprimierung</h2>

          <h3>JPEG vs. PNG für 360°-Fotos</h3>
          <p>
            <strong>JPEG</strong> ist in fast allen Fällen das richtige Format
            für 360°-Fotos. JPEGs Komprimierung reduziert Dateigrößen auf
            handhabbare Niveaus (4–25 MB für ein hochwertiges 360°-Bild) und
            alle großen 360°-Plattformen erwarten JPEG-Eingaben. Die
            XMP-Metadaten, die erforderlich sind, um eine Datei als 360°-Bild zu
            identifizieren, sind im JPEG-Header eingebettet – Apps und Kameras
            erledigen dies automatisch.
          </p>
          <p>
            <strong>PNG</strong> ist verlustfrei, erzeugt aber enorme
            Dateigrößen für 360°-Bilder (oft 100–400 MB) ohne sichtbare
            Qualitätsverbesserung gegenüber hochwertigem JPEG bei gleicher
            Auflösung. Verwenden Sie kein PNG für 360°-Virtual-Tour-Bilder.
          </p>

          <h3>JPEG-Qualitätseinstellung</h3>
          <p>
            Beim manuellen Exportieren von 360°-Bildern: JPEG-Qualität
            <strong>85–92 %</strong> verwenden. Dieser Bereich bietet visuell
            verlustfreie Qualität bei handhabaren Dateigrößen. Die Qualität
            unter 80 % zu reduzieren spart minimalen zusätzlichen Speicherplatz,
            während sichtbare Komprimierungsartefakte eingeführt werden
            (Blockbildung und Banding), die in großen einfarbigen Bereichen wie
            Himmeln, Decken und Wänden besonders auffällig sind.
          </p>
        </section>

        <section>
          <h2>Smartphone 360°-Foto-Auflösungsausgabe</h2>

          <h3>Was moderne Smartphones produzieren</h3>
          <p>
            Smartphone 360°-Apps mit geführter Multi-Frame-Aufnahme produzieren
            typischerweise Ausgaben im Bereich von
            <strong>6000 × 3000 bis 10000 × 5000 Pixeln</strong>, abhängig von
            der Kameraauflösung des Telefons und der Anzahl der in der Sequenz
            aufgenommenen Frames. High-End-Smartphones (iPhone 15 Pro, Samsung
            S24 Ultra, Google Pixel 8 Pro), die Travvir mit maximaler Qualität
            ausführen, produzieren Ausgaben im
            <strong>8000–10000-Pixel-Weitenbereich</strong>
            – was alle wichtigen Plattform-Qualitätsanforderungen erfüllt oder
            übertrifft.
          </p>

          <h3>Dedizierte 360°-Kamera-Ausgabe</h3>
          <p>
            Einstiegsniveau: Ricoh Theta SC2 → 5376 × 2688 Pixel. Dies liegt
            unter ideal für hochwertiges Tour-Viewing, ist aber für die meisten
            Geschäftsanwendungen akzeptabel. Mittelklasse: Insta360 One RS
            1-Inch → 7680 × 3840 Pixel. High-End: Insta360 Pro 2 → 8000 × 4000
            pro Linse, zusammengenäht zu ca. 11520 × 5760 Pixeln.
          </p>
        </section>

        <section>
          <h2>Fazit: Auflösung an Plattform und Zweck anpassen</h2>
          <p>
            Die praktische Empfehlung: Nehmen Sie auf mit den maximalen
            Qualitätseinstellungen, die Ihr Telefon unterstützt, exportieren Sie
            mit JPEG 90 % Qualität und überprüfen Sie die Dateiabmessung vor dem
            Hochladen, um sicherzustellen, dass sie den
            Plattform-Mindestanforderungen entspricht (mindestens 6000 × 3000
            für den Google Street View-Qualitätsschwellenwert). Für
            Travvir-Touren verarbeitet die App die Optimierung automatisch –
            wählen Sie einfach die höchste verfügbare Qualität in der App aus.
          </p>
          <p>
            Obsessieren Sie sich nicht mit der Auflösung über 8192 × 4096 für
            Geschäfts-Virtual-Tour-Anwendungen – jenseits dieses Punktes ist die
            Qualitätsverbesserung für die Mehrheit der Betrachter auf
            Standardgeräten unsichtbar, und die Dateigrößenzunahme erzeugt
            unnötige Ladeverzögerungen.
          </p>
        </section>

        
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/unscharfe-360-fotos-beheben">Unscharfe 360°-Fotos beheben : Ursachen & Lösungen</a></li>
            <li><a href="/blog/de/was-ist-ein-360-foto">Was ist ein 360°-Foto? Vollständige Erklärung</a></li>
            <li><a href="/blog/de/360-virtual-tour-website-einbetten">360°-Virtual-Tour in Website einbetten</a></li>
            <li><a href="/blog/de/360-fotografie-fehler-vermeiden">360°-Fotografie: 9 Fehler vermeiden</a></li>
            <li><a href="/blog/de/beste-smartphone-virtual-tour-app">Beste Smartphone Virtual-Tour-App : Vergleich</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-virtual-tour-bildgroesse",
);

useBlogSeo({
  title: "360°-Virtual-Tour-Bildgröße [2026]: Optimale Einstellungen | Travvir",
  description:
    "Welche Bildgröße für 360°-Virtual-Tours? Auflösung, Dateigröße und Komprimierung für schnelle Ladezeiten und maximale Qualität.",
  slug: "360-virtual-tour-bildgroesse",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
